import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Trash2, Phone, Mail, CalendarCheck, CalendarX, ChevronRight, Clock, Euro, Inbox, MessageCircle, Upload, Megaphone, Download, Star, BellOff, Sparkles, Tag, Moon } from "lucide-react";
import { format, parseISO } from "date-fns";
import { nl } from "date-fns/locale";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Calendar } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { CalendarDays } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";

const EMPTY = { name: "", email: "", phone: "", notes: "", categories: [] };

const HATCH = { backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(94,75,123,0.22) 4px, rgba(94,75,123,0.22) 8px)" };

const waNumber = (phone) => {
  let p = (phone || "").replace(/[^\d+]/g, "");
  if (p.startsWith("+")) p = p.slice(1);
  else if (p.startsWith("00")) p = p.slice(2);
  else if (p.startsWith("0")) p = "31" + p.slice(1);
  return p;
};

export default function Customers() {
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY);
  const [profile, setProfile] = useState(null); // customer being viewed
  const [importOpen, setImportOpen] = useState(false);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [winbackOpen, setWinbackOpen] = useState(false);
  const [allCategories, setAllCategories] = useState([]);
  const [catFilter, setCatFilter] = useState("all");
  useEffect(() => { api.get("/admin/categories").then((r) => setAllCategories(r.data.categories || [])).catch(() => {}); }, []);

  const load = useCallback((s = "") => api.get("/admin/customers", { params: { search: s } }).then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const klant = searchParams.get("klant");
    if (klant && list.length) {
      const c = list.find((x) => x.id === klant);
      if (c) { setProfile(c); setSearchParams({}, { replace: true }); }
    }
    if (searchParams.get("winback") === "1") { setWinbackOpen(true); setSearchParams({}, { replace: true }); }
    const catq = searchParams.get("categorie");
    if (catq) { setCatFilter(catq); setSearchParams({}, { replace: true }); }
  }, [list, searchParams, setSearchParams]);

  const createCust = async () => {
    if (!createForm.name) { toast.error("Naam is verplicht."); return; }
    try {
      await api.post("/admin/customers", createForm);
      toast.success("Klant toegevoegd.");
      setCreateOpen(false); setCreateForm(EMPTY); load(search);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const shown = catFilter === "all" ? list
    : catFilter === "__dormant" ? list.filter((c) => c.dormant)
    : list.filter((c) => [...(c.categories || []), ...(c.auto_categories || [])].includes(catFilter));

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-6 flex-wrap">
        <h1 className="text-3xl font-medium tracking-tight">Klanten</h1>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setWinbackOpen(true)} data-testid="winback-btn" className="rounded-full border-[#E8E2D9]">
            <Moon className="w-4 h-4 mr-1" /> Slapend benaderen
          </Button>
          <Button variant="outline" onClick={() => setBroadcastOpen(true)} data-testid="broadcast-btn" className="rounded-full border-[#E8E2D9]">
            <Megaphone className="w-4 h-4 mr-1" /> Bericht sturen
          </Button>
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-btn" className="rounded-full border-[#E8E2D9]">
            <Upload className="w-4 h-4 mr-1" /> Importeren
          </Button>
          <Button onClick={() => { setCreateForm(EMPTY); setCreateOpen(true); }} data-testid="add-customer-btn" className="rounded-full bg-brand hover:bg-brand-hover text-white">
            <Plus className="w-4 h-4 mr-1" /> Nieuwe klant
          </Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mb-5">
        <div className="relative w-full sm:max-w-sm">
          <Search className="w-4 h-4 text-ink-muted absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="customer-search" value={search}
            onChange={(e) => { setSearch(e.target.value); load(e.target.value); }}
            placeholder="Zoek op naam, e-mail of telefoon" className="pl-9 rounded-xl border-[#E8E2D9]" />
        </div>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger data-testid="category-filter" className="w-full sm:w-56 rounded-xl border-[#E8E2D9]"><SelectValue placeholder="Alle categorieën" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle categorieën</SelectItem>
            <SelectItem value="__dormant">Alleen slapende klanten</SelectItem>
            {allCategories.map((cat) => (<SelectItem key={cat} value={cat}>{cat}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E2D9] shadow-[0_8px_30px_rgb(74,63,63,0.04)] overflow-hidden">
        {!shown.length && <div className="p-8 text-center text-ink-muted text-sm">Geen klanten gevonden.</div>}
        <div className="divide-y divide-[#F2EBE1]">
          {shown.map((c) => (
            <div key={c.id} data-testid={`customer-${c.id}`} onClick={() => setProfile(c)} role="button" tabIndex={0}
              className="w-full text-left flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 p-4 hover:bg-cream-subtle/40 transition-colors cursor-pointer">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium truncate">{c.name}</span>
                  <Badge variant="secondary" data-testid={`count-appts-${c.id}`}
                    className="bg-brand-light text-ink font-normal gap-1">
                    <CalendarCheck className="w-3 h-3" /> {c.appointments_count ?? 0} afspraken
                  </Badge>
                  <Badge variant="secondary" data-testid={`count-reservations-${c.id}`}
                    className="bg-[#EDE6F3] text-[#5E4B7B] font-normal gap-1">
                    <Inbox className="w-3 h-3" /> {c.reservations_count ?? 0} reserveringen
                  </Badge>
                  <Badge variant="secondary" data-testid={`revenue-${c.id}`}
                    className="bg-[#E4EFE6] text-[#3F6B4A] font-normal gap-1">
                    <Euro className="w-3 h-3" /> {Number(c.revenue ?? 0).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Badge>
                  {(c.cancellations_count ?? 0) > 0 && (
                    <Badge variant="secondary" data-testid={`count-cancels-${c.id}`}
                      className="bg-[#F3E1E1] text-[#9B5C5C] font-normal gap-1">
                      <CalendarX className="w-3 h-3" /> {c.cancellations_count} geannuleerd
                    </Badge>
                  )}
                  {!c.email && !c.phone && (
                    <Badge variant="secondary" data-testid={`no-contact-${c.id}`}
                      className="bg-[#F3E7D3] text-[#8A6A2F] font-normal gap-1">
                      <BellOff className="w-3 h-3" /> Geen herinneringen
                    </Badge>
                  )}
                  {c.dormant && (
                    <Badge variant="secondary" data-testid={`dormant-${c.id}`}
                      className="bg-[#E7E3DD] text-[#6B6257] font-normal gap-1">
                      <Moon className="w-3 h-3" /> Slapend
                    </Badge>
                  )}
                  {[...new Set([...(c.categories || []), ...(c.auto_categories || [])])].map((cat) => (
                    <Badge key={cat} variant="secondary" data-testid={`cust-cat-${c.id}-${cat}`}
                      style={c.dormant ? HATCH : undefined}
                      className={`bg-[#EAE3F2] text-[#5E4B7B] font-normal gap-1 ${c.dormant ? "opacity-70 line-through" : ""}`}>
                      <Tag className="w-3 h-3" /> {cat}{(c.auto_categories || []).includes(cat) && !(c.categories || []).includes(cat) ? " · auto" : ""}
                    </Badge>
                  ))}
                </div>
                <div className="flex flex-wrap gap-x-4 text-sm text-ink-muted mt-0.5">
                  {c.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {c.email}</span>}
                  {c.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {c.phone}</span>}
                  {!c.email && !c.phone && <span className="italic">Geen contactgegevens</span>}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0 self-end sm:self-auto">
                {c.phone && (
                  <a href={`tel:${c.phone}`} onClick={(e) => e.stopPropagation()} data-testid={`call-${c.id}`}
                    title="Bellen" className="w-9 h-9 rounded-full flex items-center justify-center text-brand hover:bg-brand-light transition-colors"><Phone className="w-4 h-4" /></a>
                )}
                {c.phone && (
                  <a href={`https://wa.me/${waNumber(c.phone)}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} data-testid={`whatsapp-${c.id}`}
                    title="WhatsApp" className="w-9 h-9 rounded-full flex items-center justify-center text-[#25976C] hover:bg-[#E4EFE6] transition-colors"><MessageCircle className="w-4 h-4" /></a>
                )}
                {c.email && (
                  <a href={`mailto:${c.email}`} onClick={(e) => e.stopPropagation()} data-testid={`email-${c.id}`}
                    title="E-mailen" className="w-9 h-9 rounded-full flex items-center justify-center text-ink-muted hover:bg-cream-subtle transition-colors"><Mail className="w-4 h-4" /></a>
                )}
                <ChevronRight className="w-5 h-5 text-ink-muted ml-1" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {profile && (
        <ProfileDialog customer={profile} allCategories={allCategories} onClose={() => setProfile(null)}
          onChanged={() => load(search)} />
      )}

      {importOpen && <ImportDialog onClose={() => setImportOpen(false)} onDone={() => { setImportOpen(false); load(search); }} />}
      {broadcastOpen && <BroadcastDialog customers={list} onClose={() => setBroadcastOpen(false)} />}
      {winbackOpen && <WinBackDialog customers={list} allCategories={allCategories} onClose={() => setWinbackOpen(false)} onDone={() => load(search)} />}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-2xl max-h-[85vh] overflow-y-auto no-scrollbar" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-display">Nieuwe klant</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-sm text-ink-muted mb-1.5 block">Naam *</Label><Input data-testid="cust-name" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} className="rounded-xl border-[#E8E2D9]" /></div>
            <div><Label className="text-sm text-ink-muted mb-1.5 block">E-mail</Label><Input data-testid="cust-email" type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} className="rounded-xl border-[#E8E2D9]" /></div>
            <div><Label className="text-sm text-ink-muted mb-1.5 block">Telefoon</Label><Input data-testid="cust-phone" value={createForm.phone} onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })} className="rounded-xl border-[#E8E2D9]" /></div>
            <p className="text-xs text-ink-muted -mt-1">E-mail en telefoon zijn optioneel. Zonder deze gegevens kunnen we geen herinneringen of bevestigingen sturen.</p>
            <div><Label className="text-sm text-ink-muted mb-1.5 block">Notities</Label><Textarea data-testid="cust-notes" value={createForm.notes} onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })} className="rounded-xl border-[#E8E2D9]" rows={3} /></div>
            <div>
              <Label className="text-sm text-ink-muted mb-1.5 block">Behandelcategorieën</Label>
              <div className="flex flex-wrap gap-2">
                {allCategories.map((cat) => {
                  const on = (createForm.categories || []).includes(cat);
                  return (
                    <button key={cat} type="button" data-testid={`create-cat-${cat}`}
                      onClick={() => setCreateForm({ ...createForm, categories: on ? createForm.categories.filter((x) => x !== cat) : [...(createForm.categories || []), cat] })}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${on ? "bg-brand text-white border-brand" : "bg-white text-ink-muted border-[#E8E2D9] hover:border-brand"}`}>{cat}</button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} className="rounded-full">Annuleren</Button>
            <Button onClick={createCust} data-testid="save-customer-btn" className="rounded-full bg-brand hover:bg-brand-hover text-white">Opslaan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProfileDialog({ customer, allCategories = [], onClose, onChanged }) {
  const [form, setForm] = useState({
    name: customer.name, email: customer.email, phone: customer.phone,
    notes: customer.notes || "", notify_email: customer.notify_email !== false, notify_sms: customer.notify_sms !== false,
    categories: customer.categories || [],
  });
  const [appointments, setAppointments] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);

  const loadAppointments = () =>
    api.get(`/admin/customers/${customer.id}/appointments`).then((r) => setAppointments(r.data)).catch(() => setAppointments([]));
  useEffect(() => {
    loadAppointments();
    api.get("/admin/settings").then((r) => setReasons(r.data.cancel_reasons || [])).catch(() => {});
  }, [customer.id]);

  const persist = async (next) => {
    if (!next.name) { toast.error("Naam is verplicht."); return; }
    try {
      await api.put(`/admin/customers/${customer.id}`, next);
      toast.success("Opgeslagen", { duration: 1200 });
      onChanged();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const onField = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const onBlurSave = () => persist(form);
  const onToggle = (k) => (v) => { const next = { ...form, [k]: v }; setForm(next); persist(next); };
  const toggleCat = (cat) => {
    const cur = form.categories || [];
    const next = cur.includes(cat) ? cur.filter((x) => x !== cat) : [...cur, cat];
    const nf = { ...form, categories: next }; setForm(nf); persist(nf);
  };

  const remove = async () => {
    if (!window.confirm(`Klant ${customer.name} verwijderen?`)) return;
    await api.delete(`/admin/customers/${customer.id}`);
    toast.success("Klant verwijderd."); onChanged(); onClose();
  };

  const markNoShow = async (a) => {
    await api.put(`/admin/appointments/${a.id}`, { status: "no_show" });
    toast.success("Gemarkeerd als niet verschenen."); loadAppointments(); onChanged();
  };

  const now = new Date();
  const all = appointments || [];
  const upcoming = all.filter((a) => ["confirmed", "reschedule_pending"].includes(a.status) && parseISO(a.start) >= now)
    .sort((x, y) => (x.start < y.start ? -1 : 1));
  const past = all.filter((a) => !(["confirmed", "reschedule_pending"].includes(a.status) && parseISO(a.start) >= now));
  const done = past.filter((a) => !["cancelled", "no_show"].includes(a.status));
  const cancels = past.filter((a) => a.status === "cancelled");
  const noShows = past.filter((a) => a.status === "no_show");
  const revenue = done.reduce((sum, a) => sum + (a.price || 0), 0);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-w-lg max-h-[85vh] overflow-y-auto no-scrollbar" data-testid="customer-profile">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            {customer.name}
            {customer.dormant && (
              <span data-testid="profile-dormant" className="inline-flex items-center gap-1 text-xs font-normal rounded-full bg-[#E7E3DD] text-[#6B6257] px-2 py-0.5"><Moon className="w-3 h-3" /> Slapend</span>
            )}
          </DialogTitle>
          <DialogDescription>
            Klantprofiel met reserveringen, gegevens en historie.
            {customer.last_visit ? ` Laatste bezoek: ${format(parseISO(customer.last_visit), "d MMM yyyy", { locale: nl })}.` : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <a href={`tel:${form.phone}`} data-testid="profile-call" className="flex-1 flex items-center justify-center gap-1.5 rounded-full bg-brand-light text-brand py-2 text-sm hover:bg-brand hover:text-white transition-colors"><Phone className="w-4 h-4" /> Bellen</a>
          <a href={`https://wa.me/${waNumber(form.phone)}`} target="_blank" rel="noreferrer" data-testid="profile-whatsapp" className="flex-1 flex items-center justify-center gap-1.5 rounded-full bg-[#E4EFE6] text-[#25976C] py-2 text-sm hover:bg-[#25976C] hover:text-white transition-colors"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
          <a href={`mailto:${form.email}`} data-testid="profile-email-btn" className="flex-1 flex items-center justify-center gap-1.5 rounded-full bg-cream-subtle text-ink py-2 text-sm hover:bg-ink hover:text-white transition-colors"><Mail className="w-4 h-4" /> E-mail</a>
        </div>
        <button onClick={() => setReviewOpen(true)} data-testid="profile-review-request-btn"
          className="w-full flex items-center justify-center gap-1.5 rounded-full bg-amber-50 text-amber-700 py-2 text-sm hover:bg-amber-400 hover:text-white transition-colors border border-amber-200">
          <Star className="w-4 h-4" /> Vraag om een review
        </button>
        <Tabs defaultValue="reserveringen">
          <TabsList className="grid grid-cols-3 rounded-full bg-cream-subtle/70">
            <TabsTrigger value="gegevens" data-testid="tab-gegevens" className="rounded-full">Gegevens</TabsTrigger>
            <TabsTrigger value="reserveringen" data-testid="tab-reserveringen" className="rounded-full">Reserveringen</TabsTrigger>
            <TabsTrigger value="historie" data-testid="tab-historie" className="rounded-full">Historie</TabsTrigger>
          </TabsList>

          <TabsContent value="gegevens" className="space-y-4 pt-4">
            <p className="text-xs text-ink-muted -mt-1">Wijzigingen worden automatisch opgeslagen.</p>
            <div><Label className="text-sm text-ink-muted mb-1.5 block">Naam *</Label><Input data-testid="profile-name" value={form.name} onChange={onField("name")} onBlur={onBlurSave} className="rounded-xl border-[#E8E2D9]" /></div>
            <div><Label className="text-sm text-ink-muted mb-1.5 block">E-mail</Label><Input data-testid="profile-email" type="email" value={form.email} onChange={onField("email")} onBlur={onBlurSave} className="rounded-xl border-[#E8E2D9]" /></div>
            <div><Label className="text-sm text-ink-muted mb-1.5 block">Telefoon</Label><Input data-testid="profile-phone" value={form.phone} onChange={onField("phone")} onBlur={onBlurSave} className="rounded-xl border-[#E8E2D9]" /></div>
            <Button variant="outline" onClick={() => window.location.assign(`/admin/agenda?assist=1&customer=${customer.id}`)} data-testid="profile-plan-assistant" className="w-full rounded-full border-brand/30 bg-brand-light text-brand-hover hover:bg-brand hover:text-white"><Sparkles className="w-4 h-4 mr-2" /> Plan met assistent</Button>
            <div><Label className="text-sm text-ink-muted mb-1.5 block">Notities</Label><Textarea data-testid="profile-notes" value={form.notes} onChange={onField("notes")} onBlur={onBlurSave} className="rounded-xl border-[#E8E2D9]" rows={2} /></div>

            <div>
              <Label className="text-sm text-ink-muted mb-1.5 block">Behandelcategorieën</Label>
              <div className="flex flex-wrap gap-2">
                {allCategories.map((cat) => {
                  const on = (form.categories || []).includes(cat);
                  const auto = (customer.auto_categories || []).includes(cat);
                  const marked = on || auto;
                  return (
                    <button key={cat} type="button" data-testid={`profile-cat-${cat}`} onClick={() => toggleCat(cat)}
                      style={customer.dormant && marked ? HATCH : undefined}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${customer.dormant && marked ? "opacity-70 line-through" : ""} ${on ? "bg-brand text-white border-brand" : auto ? "bg-[#EAE3F2] text-[#5E4B7B] border-[#D9CEE9]" : "bg-white text-ink-muted border-[#E8E2D9] hover:border-brand"}`}>
                      {cat}{auto && !on ? " · auto" : ""}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-ink-muted mt-1">Klik om te koppelen/ontkoppelen. "auto" = automatisch gekoppeld omdat deze klant hier ≥ 2 afspraken heeft.{customer.dormant ? " Gearceerd = slapende klant (herleidbaar waar die in zat)." : ""}</p>
            </div>

            {!form.email && !form.phone && (
              <div data-testid="profile-no-contact" className="flex items-start gap-2 rounded-xl bg-[#F3E7D3]/60 text-[#8A6A2F] text-xs px-3 py-2">
                <BellOff className="w-4 h-4 mt-0.5 shrink-0" /> Zonder e-mail of telefoon ontvangt deze klant geen bevestigingen of herinneringen.
              </div>
            )}

            <div className="rounded-xl border border-[#E8E2D9] divide-y divide-[#F2EBE1]">
              <div className="flex items-center justify-between p-3">
                <div className="text-sm flex items-center gap-2"><Mail className="w-4 h-4 text-brand" /> Herinneringen per e-mail</div>
                <Switch data-testid="profile-notify-email" checked={form.notify_email} onCheckedChange={onToggle("notify_email")} />
              </div>
              <div className="flex items-center justify-between p-3">
                <div className="text-sm flex items-center gap-2"><Phone className="w-4 h-4 text-brand" /> Herinneringen per SMS</div>
                <Switch data-testid="profile-notify-sms" checked={form.notify_sms} onCheckedChange={onToggle("notify_sms")} />
              </div>
            </div>

            <Button variant="outline" onClick={remove} data-testid="profile-delete-btn"
              className="rounded-full text-destructive border-[#E8E2D9]">
              <Trash2 className="w-4 h-4 mr-1" /> Klant verwijderen
            </Button>
          </TabsContent>

          <TabsContent value="reserveringen" className="pt-4">
            {appointments === null && <div className="text-sm text-ink-muted text-center py-4">Laden…</div>}
            {appointments !== null && !upcoming.length && <div className="text-sm text-ink-muted text-center py-6">Geen aankomende afspraken.</div>}
            <div className="space-y-2 max-h-80 overflow-auto no-scrollbar">
              {upcoming.map((a) => {
                const pending = a.status === "reschedule_pending";
                return (
                  <div key={a.id} data-testid={`profile-upcoming-${a.id}`}
                    className="rounded-xl border border-[#F2EBE1] px-3 py-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{a.treatment_name}</div>
                        <div className="text-xs text-ink-muted flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {format(parseISO(a.start), "EEE d MMM yyyy · HH:mm", { locale: nl })}
                        </div>
                      </div>
                      {pending && <Badge variant="secondary" className="bg-[#F3E7D3] text-[#8A6A2F] shrink-0">Verzoek verstuurd</Badge>}
                    </div>
                    <div className="flex gap-1.5 mt-2">
                      <Button size="sm" variant="outline" onClick={() => setRescheduleTarget(a)} data-testid={`reschedule-${a.id}`} className="rounded-full text-xs h-8 border-[#E8E2D9] flex-1">Verzetten</Button>
                      <Button size="sm" variant="outline" onClick={() => markNoShow(a)} data-testid={`noshow-${a.id}`} className="rounded-full text-xs h-8 border-[#E8E2D9] flex-1">No-show</Button>
                      <Button size="sm" variant="outline" onClick={() => setCancelTarget(a)} data-testid={`cancel-${a.id}`} className="rounded-full text-xs h-8 border-[#E8E2D9] text-destructive flex-1">Annuleren</Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="historie" className="pt-4">
            <div className="grid grid-cols-4 gap-2 mb-4">
              <div className="rounded-xl bg-brand-light/60 p-3 text-center">
                <div className="text-xl font-display" data-testid="profile-appt-count">{done.length}</div>
                <div className="text-[11px] text-ink-muted">Geweest</div>
              </div>
              <div className="rounded-xl bg-[#E4EFE6]/70 p-3 text-center">
                <div className="text-xl font-display" data-testid="profile-revenue">€{revenue.toLocaleString("nl-NL", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</div>
                <div className="text-[11px] text-ink-muted">Omzet</div>
              </div>
              <div className="rounded-xl bg-[#F3E1E1]/60 p-3 text-center">
                <div className="text-xl font-display" data-testid="profile-cancel-count">{cancels.length}</div>
                <div className="text-[11px] text-ink-muted">Geann.</div>
              </div>
              <div className="rounded-xl bg-[#F3E7D3]/70 p-3 text-center">
                <div className="text-xl font-display" data-testid="profile-noshow-count">{noShows.length}</div>
                <div className="text-[11px] text-ink-muted">No-show</div>
              </div>
            </div>
            {appointments !== null && !past.length && <div className="text-sm text-ink-muted text-center py-4">Nog geen historie.</div>}
            <div className="space-y-1.5 max-h-64 overflow-auto no-scrollbar">
              {past.sort((x, y) => (x.start < y.start ? 1 : -1)).map((a) => {
                const cancelled = a.status === "cancelled";
                const noShow = a.status === "no_show";
                return (
                  <div key={a.id} data-testid={`history-${a.id}`}
                    className="flex items-center justify-between gap-3 rounded-xl border border-[#F2EBE1] px-3 py-2">
                    <div className="min-w-0">
                      <div className={`text-sm truncate ${cancelled ? "line-through text-ink-muted" : ""}`}>{a.treatment_name}</div>
                      <div className="text-xs text-ink-muted flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {format(parseISO(a.start), "d MMM yyyy · HH:mm", { locale: nl })}
                      </div>
                    </div>
                    <Badge variant="secondary" className={cancelled ? "bg-[#F3E1E1] text-[#9B5C5C]" : noShow ? "bg-[#F3E7D3] text-[#8A6A2F]" : "bg-brand-light text-ink"}>
                      {cancelled ? "Geannuleerd" : noShow ? "Niet verschenen" : "Geweest"}
                    </Badge>
                  </div>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>

        {cancelTarget && (
          <CancelDialog appt={cancelTarget} reasons={reasons}
            onClose={() => setCancelTarget(null)}
            onDone={() => { setCancelTarget(null); loadAppointments(); onChanged(); }} />
        )}
        {rescheduleTarget && (
          <RescheduleDialog appt={rescheduleTarget}
            onClose={() => setRescheduleTarget(null)}
            onDone={() => { setRescheduleTarget(null); loadAppointments(); onChanged(); }} />
        )}
        {reviewOpen && (
          <ReviewRequestDialog customer={customer}
            onClose={() => setReviewOpen(false)} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function CancelDialog({ appt, reasons, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [custom, setCustom] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    const finalReason = reason === "__custom__" ? custom : reason;
    setSaving(true);
    try {
      await api.post(`/admin/appointments/${appt.id}/cancel`, { reason: finalReason, notify: true });
      toast.success("Afspraak geannuleerd. Klant is geïnformeerd.");
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-w-sm max-h-[85vh] overflow-y-auto no-scrollbar" data-testid="cancel-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display">Afspraak annuleren</DialogTitle></DialogHeader>
        <p className="text-sm text-ink-muted -mt-1">{appt.treatment_name} · {format(parseISO(appt.start), "d MMM yyyy HH:mm", { locale: nl })}</p>
        <div className="space-y-2">
          <Label className="text-sm text-ink-muted">Reden</Label>
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger className="rounded-xl border-[#E8E2D9]" data-testid="cancel-reason"><SelectValue placeholder="Kies een reden" /></SelectTrigger>
            <SelectContent>
              {reasons.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              <SelectItem value="__custom__">Anders…</SelectItem>
            </SelectContent>
          </Select>
          {reason === "__custom__" && <Input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Eigen reden" data-testid="cancel-custom-reason" className="rounded-xl border-[#E8E2D9]" />}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose}>Terug</Button>
          <Button onClick={submit} disabled={saving} data-testid="cancel-confirm" className="rounded-full bg-destructive hover:bg-destructive/90 text-white">Annuleren & melden</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RescheduleDialog({ appt, onClose, onDone }) {
  const cur = parseISO(appt.start);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const [date, setDate] = useState(cur);
  const [time, setTime] = useState(format(cur, "HH:mm"));
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/appointments/${appt.id}/propose-reschedule`, { start: `${format(date, "yyyy-MM-dd")}T${time}:00`, notify: true });
      toast.success("Voorstel verstuurd. Klant bevestigt via e-mail/SMS.");
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-w-sm max-h-[85vh] overflow-y-auto no-scrollbar" data-testid="reschedule-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display">Afspraak verzetten</DialogTitle></DialogHeader>
        <p className="text-sm text-ink-muted -mt-1">Nu: {format(cur, "d MMM yyyy HH:mm", { locale: nl })}</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-sm text-ink-muted mb-1.5 block">Nieuwe datum</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button type="button" variant="outline" data-testid="reschedule-date"
                  className="w-full justify-start rounded-xl border-[#E8E2D9] font-normal">
                  <CalendarDays className="w-4 h-4 mr-2 text-brand" />
                  {format(date, "d MMM yyyy", { locale: nl })}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)} locale={nl}
                  captionLayout="dropdown-buttons" fromYear={today.getFullYear()} toYear={today.getFullYear() + 3}
                  disabled={(d) => d < today} defaultMonth={date}
                  classNames={{
                    caption: "flex justify-center pt-1 relative items-center",
                    caption_dropdowns: "flex gap-2",
                    caption_label: "hidden",
                    vhidden: "sr-only",
                    dropdown: "rdp-dropdown bg-white border border-[#E8E2D9] rounded-lg px-2 py-1 text-sm capitalize focus:outline-none focus:ring-2 focus:ring-brand/40",
                    dropdown_month: "relative",
                    dropdown_year: "relative",
                  }} />
              </PopoverContent>
            </Popover>
          </div>
          <div><Label className="text-sm text-ink-muted mb-1.5 block">Nieuwe tijd</Label><Input type="time" step="900" value={time} onChange={(e) => setTime(e.target.value)} data-testid="reschedule-time" className="rounded-xl border-[#E8E2D9]" /></div>
        </div>
        <p className="text-xs text-ink-muted">De klant krijgt een e-mail/SMS en bevestigt de nieuwe tijd met één klik.</p>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose}>Terug</Button>
          <Button onClick={submit} disabled={saving} data-testid="reschedule-confirm" className="rounded-full bg-brand hover:bg-brand-hover text-white">Voorstel versturen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReviewRequestDialog({ customer, onClose }) {
  const DEFAULT_NOTE = "Het is helemaal vrijblijvend, maar het zou mij enorm helpen!";
  const [note, setNote] = useState(DEFAULT_NOTE);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/admin/customers/${customer.id}/review-request`, { note });
      toast.success(`Review-aanvraag verstuurd via ${(r.data.sent || []).join(" en ") || "bericht"}.`);
      onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-w-sm max-h-[85vh] overflow-y-auto no-scrollbar" data-testid="review-request-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Vraag om een review</DialogTitle>
          <DialogDescription>{customer.name} ontvangt een vriendelijk verzoek via e-mail en/of SMS met een link naar Google.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label className="text-sm text-ink-muted">Toelichting (optioneel)</Label>
          <Textarea data-testid="review-request-note" value={note} onChange={(e) => setNote(e.target.value)}
            rows={3} placeholder="bijv. Het is vrijblijvend, maar zou mij enorm helpen." className="rounded-xl border-[#E8E2D9]" />
        </div>
        <p className="text-xs text-ink-muted">Verzending volgt de voorkeuren (e-mail/SMS) van de klant. De Google-reviewlink stel je in bij Instellingen.</p>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose}>Terug</Button>
          <Button onClick={submit} disabled={saving} data-testid="review-request-send" className="rounded-full bg-brand hover:bg-brand-hover text-white">
            {saving ? "Versturen…" : "Verstuur verzoek"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function parseCsv(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) return [];
  const delim = (lines[0].match(/;/g) || []).length > (lines[0].match(/,/g) || []).length ? ";" : ",";
  const headers = lines[0].split(delim).map((h) => h.trim().toLowerCase());
  const idx = (names) => headers.findIndex((h) => names.includes(h));
  const iName = idx(["naam", "name"]);
  const iEmail = idx(["email", "e-mail", "mail"]);
  const iPhone = idx(["telefoon", "phone", "tel", "telefoonnummer"]);
  const iNotes = idx(["notities", "notes", "notitie"]);
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const c = lines[i].split(delim);
    rows.push({
      name: (c[iName] || "").trim(),
      email: (c[iEmail] || "").trim(),
      phone: (c[iPhone] || "").trim(),
      notes: iNotes >= 0 ? (c[iNotes] || "").trim() : "",
    });
  }
  return rows.filter((r) => r.name || r.email || r.phone);
}

function ImportDialog({ onClose, onDone }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const downloadTemplate = () => {
    const csv = "naam,email,telefoon,notities\nJan Jansen,jan@example.com,0612345678,VIP klant\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "klanten-template.csv";
    a.click();
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => { setRows(parseCsv(ev.target.result)); };
    reader.readAsText(file);
  };

  const doImport = async () => {
    if (!rows.length) { toast.error("Geen rijen gevonden."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/customers/import", rows);
      toast.success(`${data.created} toegevoegd, ${data.updated} bijgewerkt${data.skipped ? `, ${data.skipped} overgeslagen` : ""}.`);
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-h-[85vh] overflow-y-auto no-scrollbar" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Klanten importeren</DialogTitle>
          <DialogDescription>Importeer klanten in bulk via een CSV-bestand.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-ink-muted">Upload een CSV-bestand met kolommen: naam, email, telefoon, notities.</p>
          <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn" className="rounded-full border-[#E8E2D9] w-full">
            <Download className="w-4 h-4 mr-1" /> Template downloaden
          </Button>
          <input type="file" accept=".csv,text/csv" onChange={onFile} data-testid="import-file-input"
            className="block w-full text-sm text-ink-muted file:mr-3 file:rounded-full file:border-0 file:bg-brand-light file:px-4 file:py-2 file:text-brand" />
          {rows.length > 0 && <p className="text-sm text-ink" data-testid="import-preview-count">{rows.length} klant(en) gevonden in het bestand.</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose}>Annuleren</Button>
          <Button onClick={doImport} disabled={busy || !rows.length} data-testid="import-confirm-btn" className="rounded-full bg-brand hover:bg-brand-hover text-white">Importeren</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WinBackDialog({ customers, allCategories = [], onClose, onDone }) {
  const [cat, setCat] = useState("all");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [skipRecent, setSkipRecent] = useState(true);

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      setSubject(r.data.winback_subject || "We missen je bij Nelleke Holt");
      setBody(r.data.winback_body || "");
    }).catch(() => {});
  }, []);

  const targets = customers.filter((c) => c.dormant && (cat === "all" || [...(c.categories || []), ...(c.auto_categories || [])].includes(cat)));
  const now = Date.now();
  const isRecent = (c) => c.last_winback && (now - new Date(c.last_winback).getTime()) < 30 * 24 * 3600 * 1000;
  const recentCount = targets.filter(isRecent).length;
  const finalTargets = skipRecent ? targets.filter((c) => !isRecent(c)) : targets;

  const send = async () => {
    if (!finalTargets.length) { toast.error("Geen klanten om te benaderen."); return; }
    if (!subject || !body) { toast.error("Onderwerp en bericht zijn verplicht."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/winback/send", { subject, body, customer_ids: finalTargets.map((c) => c.id) });
      toast.success(`Verstuurd naar ${data.email_sent} slapende klant(en) per e-mail. Notitie "benaderd" toegevoegd.`);
      onDone && onDone();
      onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const sendTest = async () => {
    if (!subject || !body) { toast.error("Onderwerp en bericht zijn verplicht."); return; }
    setTesting(true);
    try {
      const { data } = await api.post("/admin/winback/test", { subject, body });
      toast.success(`Testmail verstuurd naar ${data.to}.`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-w-lg max-h-[85vh] overflow-y-auto no-scrollbar" data-testid="winback-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Moon className="w-5 h-5 text-brand" /> Slapende klanten benaderen</DialogTitle>
          <DialogDescription>Stuur in één keer een "we missen je"-bericht naar slapende klanten (optioneel per categorie).</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[65vh] overflow-auto no-scrollbar pr-1">
          <div>
            <Label className="text-sm text-ink-muted mb-1.5 block">Categorie</Label>
            <Select value={cat} onValueChange={setCat}>
              <SelectTrigger data-testid="winback-category" className="rounded-xl border-[#E8E2D9]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle slapende klanten</SelectItem>
                {allCategories.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="rounded-xl bg-brand-light/40 text-brand-hover text-sm px-3 py-2" data-testid="winback-count">
            {finalTargets.length} slapende klant(en) worden benaderd{cat !== "all" ? ` in "${cat}"` : ""}.
          </div>
          {recentCount > 0 && (
            <div className="flex items-center justify-between gap-3 rounded-xl border border-[#E8E2D9] px-3 py-2">
              <span className="text-sm text-ink-muted">Sla {recentCount} klant(en) over die &lt; 30 dagen geleden al benaderd zijn</span>
              <Switch checked={skipRecent} onCheckedChange={setSkipRecent} data-testid="winback-skip-recent" />
            </div>
          )}
          <div className="rounded-xl bg-cream-subtle/60 text-ink-muted text-xs px-3 py-2" data-testid="winback-email-note">
            Wordt altijd via e-mail in jullie huisstijl verstuurd. De standaardtekst pas je aan bij Instellingen → Win-back bericht. Na verzending krijgt elke klant automatisch een notitie "benaderd op [datum]".
          </div>
          <div><Label className="text-sm text-ink-muted mb-1.5 block">Onderwerp</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="winback-subject" className="rounded-xl border-[#E8E2D9]" /></div>
          <div><Label className="text-sm text-ink-muted mb-1.5 block">Bericht</Label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} data-testid="winback-body" rows={7} className="rounded-xl border-[#E8E2D9]" /></div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" className="rounded-full" onClick={sendTest} disabled={testing} data-testid="winback-test-btn">{testing ? "..." : "Testmail naar mezelf"}</Button>
          <Button onClick={send} disabled={busy || !finalTargets.length} data-testid="winback-send-btn" className="rounded-full bg-brand hover:bg-brand-hover text-white">Versturen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BroadcastDialog({ customers, onClose }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [channel, setChannel] = useState("email");
  const [audience, setAudience] = useState("all");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const send = async () => {
    if (!subject || !body) { toast.error("Onderwerp en bericht zijn verplicht."); return; }
    if (audience === "selected" && !selected.length) { toast.error("Kies minstens één klant."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/broadcast", {
        subject, body, channel, audience, customer_ids: audience === "selected" ? selected : [],
      });
      toast.success(`Verstuurd naar ${data.targets} klant(en): ${data.email_sent} e-mail, ${data.sms_sent} SMS.`);
      onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-w-lg" data-testid="broadcast-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Bericht sturen (Nieuws)</DialogTitle>
          <DialogDescription>Stuur een nieuwsbericht naar je klanten via e-mail en/of SMS.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[65vh] overflow-auto no-scrollbar pr-1">
          <div>
            <Label className="text-sm text-ink-muted mb-1.5 block">Verstuur via</Label>
            <div className="flex gap-2">
              {[["email", "E-mail"], ["sms", "SMS"], ["both", "Beide"]].map(([v, l]) => (
                <button key={v} onClick={() => setChannel(v)} data-testid={`broadcast-channel-${v}`}
                  className={`flex-1 rounded-full py-2 text-sm border transition-colors ${channel === v ? "bg-brand text-white border-brand" : "border-[#E8E2D9] text-ink-muted"}`}>{l}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-sm text-ink-muted mb-1.5 block">Ontvangers</Label>
            <div className="flex gap-2">
              <button onClick={() => setAudience("all")} data-testid="broadcast-audience-all"
                className={`flex-1 rounded-full py-2 text-sm border transition-colors ${audience === "all" ? "bg-brand text-white border-brand" : "border-[#E8E2D9] text-ink-muted"}`}>Iedereen ({customers.length})</button>
              <button onClick={() => setAudience("selected")} data-testid="broadcast-audience-selected"
                className={`flex-1 rounded-full py-2 text-sm border transition-colors ${audience === "selected" ? "bg-brand text-white border-brand" : "border-[#E8E2D9] text-ink-muted"}`}>Selectie ({selected.length})</button>
            </div>
          </div>
          {audience === "selected" && (
            <div className="rounded-xl border border-[#E8E2D9] max-h-40 overflow-auto divide-y divide-[#F2EBE1]">
              {customers.map((c) => (
                <label key={c.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-cream-subtle/40">
                  <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggle(c.id)} data-testid={`broadcast-select-${c.id}`} />
                  {c.name}
                </label>
              ))}
            </div>
          )}
          <div><Label className="text-sm text-ink-muted mb-1.5 block">Onderwerp</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="broadcast-subject" className="rounded-xl border-[#E8E2D9]" /></div>
          <div><Label className="text-sm text-ink-muted mb-1.5 block">Bericht</Label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} data-testid="broadcast-body" rows={5} className="rounded-xl border-[#E8E2D9]" /></div>
          {channel !== "email" && <p className="text-xs text-ink-muted">Via SMS sturen we het onderwerp met een link naar het volledige bericht.</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose}>Annuleren</Button>
          <Button onClick={send} disabled={busy} data-testid="broadcast-send-btn" className="rounded-full bg-brand hover:bg-brand-hover text-white">Versturen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

